import Tickettype from '../models/Tickettype';
import Screen from './Screen';
import {
    PREVIEW_PEOPLE,
    buildPreviewSimulation,
} from './pdfLayoutPreviewSample';

import portrait_01 from '../assets/images/preview_portraits/portrait_01.webp';
import portrait_02 from '../assets/images/preview_portraits/portrait_02.webp';
import portrait_03 from '../assets/images/preview_portraits/portrait_03.webp';
import portrait_04 from '../assets/images/preview_portraits/portrait_04.webp';
import portrait_01_thermal from '../assets/images/preview_portraits/portrait_01_thermal.png';
import portrait_02_thermal from '../assets/images/preview_portraits/portrait_02_thermal.png';
import portrait_03_thermal from '../assets/images/preview_portraits/portrait_03_thermal.png';
import portrait_04_thermal from '../assets/images/preview_portraits/portrait_04_thermal.png';

export const PREVIEW_PORTRAITS_COLOR = [
    portrait_01,
    portrait_02,
    portrait_03,
    portrait_04,
];

export const PREVIEW_PORTRAITS_THERMAL = [
    portrait_01_thermal,
    portrait_02_thermal,
    portrait_03_thermal,
    portrait_04_thermal,
];

const TEMPLATE_PORTRAIT_INDEX = {
    otp_a4: 0,
    card_cr80: 1,
    card_cr80_landscape: 2,
    otp_thermal: 3,
};

function hashSeed(seed) {
    let hash = 0;
    const str = String(seed || 'preview');

    for (let i = 0; i < str.length; i++)
        hash = ((hash << 5) - hash) + str.charCodeAt(i);

    return Math.abs(hash);
}

export function isThermalPdfLayout(layoutOrTarget) {
    if (typeof layoutOrTarget === 'string')
        return layoutOrTarget === 'thermal';

    return layoutOrTarget?.target === 'thermal';
}

export function pickPreviewPortraitUrl(layoutOrTarget, seed = 'preview') {
    const portraits = isThermalPdfLayout(layoutOrTarget)
        ? PREVIEW_PORTRAITS_THERMAL
        : PREVIEW_PORTRAITS_COLOR;

    const templateIndex = TEMPLATE_PORTRAIT_INDEX[seed];
    const index = templateIndex != null
        ? templateIndex % portraits.length
        : hashSeed(seed) % portraits.length;

    return portraits[index];
}

export function thermalPdfPreviewClass(layoutOrTarget) {
    return isThermalPdfLayout(layoutOrTarget) ? ' pdf-layout-preview--thermal' : '';
}

/** Match the canvas page filter used when rendering widgets. */
export function widgetVisibleOnPage(data, pageIndex, pageCount, allWidgets = []) {
    const p = pageIndex;
    if (data.page === p)
        return true;
    if (data.page === 'all')
        return true;
    if (data.page === 'odd' && (p % 2 === 1))
        return true;
    if (data.page === 'even' && (p % 2 === 0))
        return true;
    if (data.page === 'first' && p === 0)
        return true;
    if (data.page === 'last' && p === ((pageCount || 1) - 1))
        return true;
    if (data.page === 'continuation' && p >= 1) {
        // When a same-type body exists on page:last, continuation is middle-only
        // so editor dimensions on last are authoritative (no auto-shrink).
        const last_index = (pageCount || 1) - 1;
        if (p === last_index && last_index > 0) {
            const sub = data.subType;
            if (sub === 'programme_body' || sub === 'document_body') {
                const has_last_body = (allWidgets || []).some(
                    w => w?.page === 'last' && w?.subType === sub
                );
                if (has_last_body)
                    return false;
            }
        }
        return true;
    }
    return false;
}

export {
    editorPageLabel,
    isFlowRoleLayout,
} from './pdfLayoutFlowPageLabels';

/**
 * Stable viewport width for fit-zoom (ignore scrollbar gutter).
 * Hide overflow while measuring so clientWidth does not depend on content zoom.
 */
export function measurePdfPreviewViewport(wrapperEl) {
    if (!wrapperEl || typeof window === 'undefined')
        return null;

    const computed = window.getComputedStyle(wrapperEl);
    const prevOverflow = wrapperEl.style.overflow;
    wrapperEl.style.overflow = 'hidden';

    const width = wrapperEl.clientWidth;
    const height = wrapperEl.clientHeight;

    wrapperEl.style.overflow = prevOverflow;

    const padX = (parseFloat(computed.paddingLeft) || 0)
        + (parseFloat(computed.paddingRight) || 0);
    const padY = (parseFloat(computed.paddingTop) || 0)
        + (parseFloat(computed.paddingBottom) || 0);

    return {
        width: Math.max(40, width - padX),
        height: Math.max(40, height - padY),
    };
}

/**
 * Fit-to-width (shrink-only): page width fills the editor viewport, never above 100%.
 * Same idea as PDF readers' "Fit width" with a 100% cap.
 */
export function computeFitPageScale(pageDimensions, wrapperElOrViewport, {
    min = 0.25,
    max = 1,
    step = 0.05,
} = {}) {
    if (!pageDimensions?.width || !pageDimensions?.height)
        return 1;

    const viewport = wrapperElOrViewport?.width != null && wrapperElOrViewport?.height != null
        && typeof wrapperElOrViewport.width === 'number'
        ? wrapperElOrViewport
        : measurePdfPreviewViewport(wrapperElOrViewport);

    if (!viewport)
        return 1;

    const availW = viewport.width;
    const basePpi = Screen.getBasePpi();
    const pageW = Screen.cmToInches(pageDimensions.width) * basePpi;
    if (pageW <= 0)
        return 1;

    const raw = availW / pageW;
    // Floor so the page always fits; tiny epsilon avoids 0.999 → 0.95.
    const snapped = Math.floor((raw / step) + 1e-6) * step;
    return Math.min(max, Math.max(min, snapped));
}

export function computePdfTemplatePreviewMaxSize() {
    if (typeof window === 'undefined')
        return { width: 190, height: 165 };

    const modalWidth = Math.min(820, window.innerWidth * 0.94);
    const cellInner = ((modalWidth - 24) / 2) - 20;
    const maxHeight = Math.min(230, (window.innerHeight * 0.9 - 110) / 3);

    return {
        width: Math.max(160, Math.floor(cellInner * 0.96)),
        height: Math.max(135, Math.floor(maxHeight)),
    };
}

export function buildPdfCanvasPreviewProps(templateName, lang = 'fr') {
    const isOtp = templateName?.startsWith('otp_');
    const sample = buildPreviewSimulation(lang);

    if (!isOtp)
        return sample;

    return {
        ...sample,
        tickettype: {
            _id: Tickettype.ONE_TIME_PASS,
            name: {
                fr: 'Abonnement',
                en: 'Subscription',
                de: 'Abonnement',
                it: 'Abbonamento',
            },
            description: {
                fr: 'Abonnement valable sur les séances',
                en: 'Subscription valid for screenings',
                de: 'Abonnement gültig für Vorstellungen',
                it: 'Abbonamento valido per le proiezioni',
            },
            people: PREVIEW_PEOPLE,
        },
    };
}
